import { ButtonBase, styled } from '@mui/material';
import { useState } from 'react';

import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import type { CompetitionWinner } from '@/lib/api/requests.ts';

import { SUMMER_JUNE_2024_COMPETITION_END_DATE } from '../../../config.ts';
import { CTA } from '../_components/CTA.tsx';
import { TopSectionBackground } from '../_components/TopSectionBackground.tsx';
import { useHasEnded } from '../_components/useHasEnded.ts';

const Circle = styled('span')(() => ({
  borderRadius: '9999999px',
  aspectRatio: '1 / 1',
  backgroundColor: 'white',
  display: 'inline-grid',
  height: '1.5em',
  lineHeight: 1,
  placeContent: 'center',
}));

const List = styled('ol')(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3),

  '& li': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },

  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4.5),
  },
}));

const dateFormatter = new Intl.DateTimeFormat('no', {
  day: 'numeric',
  month: 'long',
});
const dateStr = dateFormatter.format(SUMMER_JUNE_2024_COMPETITION_END_DATE);

const TextContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: 'auto',
  marginBottom: 'auto',

  [theme.breakpoints.up('sm')]: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

export const TopSectionJune = (props: { winners: CompetitionWinner[] | undefined }) => {
  const { winners } = props;
  const ended = useHasEnded();
  const [open, setOpen] = useState(false);
  const onClose = () => setOpen(false);

  return (
    <TopSectionBackground>
      <TextContainer>
        <h1 className="mb-8 typography-v1-display">
          Send gjelden
          <br />
          din på ferie
        </h1>

        <div className="mb-6 typography-v1-h4 md:mb-8">
          Skulle du ønske du kunne ta en pause fra gjelden din?
        </div>

        <div className="typography-v1-body4">
          <List>
            <li>
              <Circle>1</Circle>
              Sjekk din gjeld
            </li>
            <li>
              <Circle>2</Circle>
              Se hvor mye du betaler i løpet av et år
            </li>
            <li>
              <Circle>3</Circle>
              Delta i trekningen
            </li>
          </List>
        </div>

        <div className="mb-6 max-w-[60ch] typography-v1-h4 font-normal text-balance md:mb-8">
          {ended ? (
            <>
              Denne trekningen er nå avsluttet. Vi trakk{' '}
              <ButtonBase onClick={() => setOpen(true)}>
                <u>
                  <b>10 heldige vinnere</b>
                </u>
              </ButtonBase>{' '}
              som vi overtar regningene til i et helt år.
            </>
          ) : (
            <>
              {dateStr} skal Scoopr trekke <b>10 heldige vinnere</b> som vi{' '}
              <b>overtar regningene</b> til <b>i et helt år</b>.
            </>
          )}
        </div>

        <CTA />
      </TextContainer>
      {winners && <CompetitionWinnersDialog winners={winners} open={open} onClose={onClose} />}
    </TopSectionBackground>
  );
};
